import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Receipt, Sparkle, FileText, FilePdf, FileXls, FloppyDisk, Trash, Warning, CheckCircle,
} from "@phosphor-icons/react";
import { api, fmtDate, fmtMoney, formatApiErrorDetail, triggerFileDownload } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Field from "@/components/billing/Field";

/**
 * RABillSheet — extracted from RABilling.jsx (iter47 refactor).
 *
 * Create / edit a Running-Account bill against a Client BOQ. Includes:
 *   • Client Work Order header card (auto-populated from Project/BOQ)
 *   • Bill header (period · date · client bill # · final-bill toggle · overrides)
 *   • Optional "Generate from Evidence" button → auto-fills from linked measurement
 *     sheets + delivery challans
 *   • Measurement-sheet chips (click to auto-fill supply qty)
 *   • Per-item bill lines (edit Supply / Install / T&C qty)
 *   • Recoveries + signatures + notes
 *   • Live totals + ITC ledger on the right rail
 *   • Footer with status transitions (Verify → Approve → Submit → Mark Paid) + PDF/Excel download
 *
 * Props:
 *   • boqId   — id of the BOQ to bill against (used when creating)
 *   • raId    — existing RA-bill id (edit mode)
 *   • onClose — dismiss the sheet
 *   • onSaved — invoked after Save / status-change / delete
 */
// ---------------------------------------------------------------------------
// Helpers extracted from JSX to avoid nested ternaries (code-review iter75)
// ---------------------------------------------------------------------------
function isInterstateToDropdown(v) {
  if (v === null) return "boq";
  return v ? "yes" : "no";
}

function dropdownToInterstate(v) {
  if (v === "boq") return null;
  return v === "yes";
}

function saveButtonLabel(busy, isEdit) {
  if (busy) return "Saving…";
  return isEdit ? "Update" : "Create RA Bill";
}


export default function RABillSheet({ boqId, raId, onClose, onSaved }) {
  const [boq, setBoq] = useState(null);
  const [existingRA, setExistingRA] = useState(null);
  const [form, setForm] = useState({
    bill_period_start: "",
    bill_period_end: new Date().toISOString().slice(0, 10),
    bill_date: new Date().toISOString().slice(0, 10),
    bill_no_client: "",
    is_final_bill: false,
    is_interstate: null,
    tds_pct: null, retention_pct: null,
    car_policy_amount: 0, hold_quality: 0, hold_other: 0,
    less_secured_advance: 0, less_advance: 0, hold_other_release: 0,
    already_paid: 0,
    prepared_by: "", verified_by: "", approved_by: "",
    notes: "",
    measurement_sheet_ids: [], client_challan_ids: [],
    items: [],
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [measurementSheets, setMSheets] = useState([]);
  const [, setChallans] = useState([]);
  const [genBusy, setGenBusy] = useState(false);
  const [genCoverage, setGenCoverage] = useState(null);

  useEffect(() => {
    (async () => {
      let effectiveBoqId = boqId;
      if (raId) {
        try {
          const { data } = await api.get(`/ra-bills/${raId}`);
          setExistingRA(data);
          effectiveBoqId = data.boq_id;
          setForm((f) => ({
            ...f,
            bill_period_start: data.bill_period_start || "",
            bill_period_end: data.bill_period_end || "",
            bill_date: data.bill_date || "",
            bill_no_client: data.bill_no_client || "",
            is_final_bill: !!data.is_final_bill,
            is_interstate: data.is_interstate,
            tds_pct: data.tds_pct, retention_pct: data.retention_pct,
            car_policy_amount: data.car_policy_amount || 0,
            hold_quality: data.hold_quality || 0,
            hold_other: data.hold_other || 0,
            less_secured_advance: data.less_secured_advance || 0,
            less_advance: data.less_advance || 0,
            hold_other_release: data.hold_other_release || 0,
            already_paid: data.already_paid || 0,
            prepared_by: data.prepared_by || "",
            verified_by: data.verified_by || "",
            approved_by: data.approved_by || "",
            notes: data.notes || "",
            measurement_sheet_ids: data.measurement_sheet_ids || [],
            client_challan_ids: data.client_challan_ids || [],
            items: (data.items || []).map((it) => ({
              boq_item_index: it.boq_item_index,
              supply_this_qty: it.supply?.this_qty || 0,
              install_this_qty: it.install?.this_qty || 0,
              tnc_this_qty: it.tnc?.this_qty || 0,
              supply_pct: it.supply_pct, install_pct: it.install_pct, tnc_pct: it.tnc_pct,
            })),
          }));
          setPreview(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
      }
      if (effectiveBoqId) {
        try {
          const { data } = await api.get(`/boqs/${effectiveBoqId}`);
          setBoq(data);
          if (!raId) {
            setForm((f) => ({ ...f, items: (data.items || []).map((_, i) => ({ boq_item_index: i, supply_this_qty: 0, install_this_qty: 0, tnc_this_qty: 0 })) }));
          }
          const [ms, dc] = await Promise.all([
            api.get(`/measurement-sheets?boq_id=${effectiveBoqId}`),
            api.get(`/client-challans?boq_id=${effectiveBoqId}`),
          ]);
          setMSheets(ms.data || []);
          setChallans(dc.data || []);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
      }
    })();
  }, [boqId, raId]);

  useEffect(() => {
    if (!boq) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/ra-bills/preview", { boq_id: boq.id, ...form });
        setPreview(data);
      } catch (e) { devLog.warn(e); }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, boq?.id]);

  const patchLine = (idx, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));

  const applyMeasurementSheet = (msId) => {
    const ms = measurementSheets.find((m) => m.id === msId);
    if (!ms) return;
    setForm((f) => {
      const items = [...f.items];
      (ms.lines || []).forEach((ln) => {
        const idx = items.findIndex((it) => it.boq_item_index === ln.boq_item_index);
        if (idx >= 0) items[idx] = { ...items[idx], supply_this_qty: (Number(items[idx].supply_this_qty) || 0) + (Number(ln.measured_qty) || 0) };
      });
      const nextIds = f.measurement_sheet_ids.includes(msId) ? f.measurement_sheet_ids : [...f.measurement_sheet_ids, msId];
      return { ...f, items, measurement_sheet_ids: nextIds };
    });
    toast.success(`Measurement sheet ${ms.sheet_no} applied to Supply column — adjust as needed`);
  };

  const generateFromEvidence = async () => {
    if (!boq) return;
    if (raId) {
      if (!window.confirm("This will REPLACE current qty inputs with values auto-calculated from the linked measurement sheets & delivery challans. Continue?")) return;
    }
    setGenBusy(true);
    try {
      const { data } = await api.post("/ra-bills/generate-draft", {
        boq_id: boq.id,
        auto_pick_unlinked: true,
        supply_from_challans: true,
        cap_by_evidence: true,
      });
      const byIdx = Object.fromEntries((data.items || []).map((it) => [it.boq_item_index, it]));
      setForm((f) => ({
        ...f,
        items: (boq.items || []).map((_, i) => byIdx[i] || { boq_item_index: i, supply_this_qty: 0, install_this_qty: 0, tnc_this_qty: 0 }),
        measurement_sheet_ids: Array.from(new Set([...(f.measurement_sheet_ids || []), ...(data.used_measurement_sheet_ids || [])])),
        client_challan_ids: Array.from(new Set([...(f.client_challan_ids || []), ...(data.used_client_challan_ids || [])])),
      }));
      setGenCoverage(data);
      const filled = (data.items || []).length;
      toast.success(`Draft generated · ${filled} line(s) auto-filled · ₹${Math.round(data.estimated_gross_this_bill).toLocaleString("en-IN")} gross · ${data.measurement_sheets_used} sheet(s) + ${data.client_challans_used} challan(s) used`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setGenBusy(false); }
  };

  const save = async () => {
    if (!boq) return toast.error("BOQ not loaded");
    setBusy(true);
    try {
      const payload = { boq_id: boq.id, ...form };
      if (raId) { await api.put(`/ra-bills/${raId}`, payload); toast.success("RA bill updated"); }
      else { await api.post("/ra-bills", payload); toast.success("RA bill created"); }
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const changeStatus = async (status) => {
    if (!raId) return toast.error("Save the RA first");
    try { await api.post(`/ra-bills/${raId}/status`, { status }); toast.success(`Status → ${status}`); onSaved?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (!boq) return null;
  const boqItems = boq.items || [];

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[min(1400px,100vw)] sm:max-w-none overflow-y-auto" data-testid="ra-bill-sheet">
        <SheetHeader className="mb-4">
          <SheetTitle className="font-display flex items-center gap-2 flex-wrap">
            <Receipt size={18} className="text-[#EA580C]" />
            {raId ? `RA Bill · ${existingRA?.ra_bill_no || ""}` : "New RA Bill"} · {boq.boq_no} — {boq.boq_title}
            <Button
              size="sm"
              onClick={generateFromEvidence}
              disabled={genBusy}
              className="ml-auto h-8 rounded-sm bg-gradient-to-r from-[#EA580C] to-[#c2410c] hover:from-[#c2410c] hover:to-[#9a3412] text-white font-semibold shadow-sm"
              data-testid="ra-generate-btn"
              title="Auto-fill this bill from unbilled measurement sheets & delivery challans"
            >
              <Sparkle size={13} className="mr-1.5" /> {genBusy ? "Generating…" : "Generate from Evidence"}
            </Button>
          </SheetTitle>
        </SheetHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
          {/* LEFT — form */}
          <div className="space-y-4">
            <div className="border border-[#0F172A]/15 bg-gradient-to-br from-slate-50 to-white rounded-sm p-4" data-testid="ra-wo-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-sm bg-[#0F172A] flex items-center justify-center"><FileText size={16} className="text-white" /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Client Work Order</div>
                    <div className="font-display text-[15px] font-bold text-slate-900">{boq.work_order_no || <span className="text-slate-400 italic">No WO # · edit BOQ header to add</span>}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  {boq.wo_attachment_id && (
                    <Button size="sm" variant="outline" className="rounded-sm h-8 text-[11px]" onClick={() => triggerFileDownload(`/attachments/${boq.wo_attachment_id}/download`, "WorkOrder.pdf")} data-testid="ra-wo-download">
                      <FilePdf size={12} className="mr-1" /> Download WO
                    </Button>
                  )}
                  {boq.project_id && (
                    <Button size="sm" variant="outline" className="rounded-sm h-8 text-[11px]" onClick={() => window.open(`/projects/${boq.project_id}`, "_blank")} data-testid="ra-project-link">
                      Project ↗
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-[11.5px]">
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Client</div><div className="font-semibold text-slate-900">{boq.client_name || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Site</div><div className="text-slate-800">{boq.site || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">WO Date</div><div className="text-slate-800 font-mono">{boq.work_order_date ? fmtDate(boq.work_order_date) : "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">WO Value</div><div className="font-mono-num font-semibold text-slate-900">{fmtMoney(boq.work_order_value || 0)}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Agreement #</div><div className="font-mono text-slate-800">{boq.agreement_no || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Sub Project</div><div className="text-slate-800">{boq.sub_project || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Sub Head</div><div className="text-slate-800">{boq.sub_head || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Tax Regime</div><div className="text-slate-800">{boq.is_interstate ? "IGST 18%" : "CGST 9% + SGST 9%"}</div></div>
                {boq.client_gstin && (<div><div className="text-[10px] uppercase tracking-widest text-slate-500">Client GSTIN</div><div className="font-mono text-slate-800">{boq.client_gstin}</div></div>)}
                {boq.contractor_gstin && (<div><div className="text-[10px] uppercase tracking-widest text-slate-500">Contractor GSTIN</div><div className="font-mono text-slate-800">{boq.contractor_gstin}</div></div>)}
              </div>
            </div>

            <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Bill Header</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Bill Period Start"><Input type="date" value={form.bill_period_start} onChange={(e) => setForm({ ...form, bill_period_start: e.target.value })} data-testid="ra-period-start" /></Field>
                <Field label="Bill Period End"><Input type="date" value={form.bill_period_end} onChange={(e) => setForm({ ...form, bill_period_end: e.target.value })} /></Field>
                <Field label="Bill Date"><Input type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} data-testid="ra-bill-date" /></Field>
                <Field label="Client Bill Ref #"><Input value={form.bill_no_client} onChange={(e) => setForm({ ...form, bill_no_client: e.target.value })} className="font-mono" placeholder="e.g. 042" /></Field>
                <Field label="Final Bill?">
                  <Select value={form.is_final_bill ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, is_final_bill: v === "yes" })}>
                    <SelectTrigger data-testid="ra-final"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes · releases retention</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Interstate override">
                  <Select value={isInterstateToDropdown(form.is_interstate)} onValueChange={(v) => setForm({ ...form, is_interstate: dropdownToInterstate(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boq">Use BOQ default</SelectItem>
                      <SelectItem value="no">No · CGST + SGST</SelectItem>
                      <SelectItem value="yes">Yes · IGST</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="TDS % override"><Input type="number" value={form.tds_pct ?? ""} onChange={(e) => setForm({ ...form, tds_pct: e.target.value === "" ? null : Number(e.target.value) })} placeholder={`BOQ: ${boq.tds_pct}%`} className="font-mono-num text-right" /></Field>
                <Field label="Retention % override"><Input type="number" value={form.retention_pct ?? ""} onChange={(e) => setForm({ ...form, retention_pct: e.target.value === "" ? null : Number(e.target.value) })} placeholder={`BOQ: ${boq.retention_pct}%`} className="font-mono-num text-right" /></Field>
              </div>
            </div>

            {genCoverage && (
              <div className="border border-[#EA580C]/30 bg-[#EA580C]/5 rounded-sm p-3 space-y-2" data-testid="ra-coverage-panel">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-[#EA580C] flex items-center gap-2"><Sparkle size={12} /> Draft auto-generated from evidence</div>
                  <button className="text-[11px] text-slate-500 hover:text-slate-700" onClick={() => setGenCoverage(null)}>Dismiss ✕</button>
                </div>
                <div className="text-[11.5px] text-slate-700">
                  Filled <b>{(genCoverage.items || []).length}</b> line(s) from <b>{genCoverage.measurement_sheets_used}</b> measurement sheet(s) + <b>{genCoverage.client_challans_used}</b> delivery challan(s).
                  Estimated gross <b className="font-mono-num">{fmtMoney(genCoverage.estimated_gross_this_bill)}</b>.
                  <span className="text-slate-500 ml-1">Review each line below and adjust as needed — the wizard is fully editable.</span>
                </div>
                {(genCoverage.coverage || []).some((c) => c.capped_by_boq) && (
                  <div className="text-[11px] text-amber-700 flex items-start gap-1"><Warning size={12} className="mt-[2px] shrink-0" /> Some lines were capped by BOQ contract qty — evidence exceeds contract; adjust manually if you want to raise a change order.</div>
                )}
              </div>
            )}

            {measurementSheets.length > 0 && (
              <div className="border border-slate-200 rounded-sm bg-slate-50 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Signed Measurement Sheets</div>
                  <div className="text-[10.5px] text-slate-500">Click to auto-fill Supply qty</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {measurementSheets.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => applyMeasurementSheet(m.id)}
                      className={`text-[11px] rounded-sm px-2 py-1 border font-mono ${form.measurement_sheet_ids.includes(m.id) ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-slate-300 text-slate-700 hover:border-[#EA580C]"}`}
                      data-testid={`ra-ms-${m.id}`}
                    >
                      {m.sheet_no} · {m.signed_by_client ? "✓ signed" : "unsigned"} · ₹{Math.round(m.total_measured_value)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="border border-slate-200 rounded-sm bg-white overflow-x-auto">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-widest text-slate-600">
                Bill Lines (edit Supply / Install / T&C qty per BOQ item)
              </div>
              <Table className="bc-list">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-14">SR</TableHead>
                    <TableHead className="w-64">Description</TableHead>
                    <TableHead className="w-16">Unit</TableHead>
                    <TableHead className="w-16 text-right">Qty</TableHead>
                    <TableHead className="w-24 text-right">Rate</TableHead>
                    <TableHead className="w-24 text-right">Supply Qty</TableHead>
                    <TableHead className="w-24 text-right">Install Qty</TableHead>
                    <TableHead className="w-24 text-right">T&C Qty</TableHead>
                    <TableHead className="w-28 text-right">This Bill ₹</TableHead>
                    <TableHead className="w-16 text-center">⚠</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {boqItems.map((b, idx) => {
                    const line = form.items.find((it) => it.boq_item_index === idx) || { boq_item_index: idx, supply_this_qty: 0, install_this_qty: 0, tnc_this_qty: 0 };
                    const raLine = (preview?.items || []).find((it) => it.boq_item_index === idx);
                    const localIdx = form.items.findIndex((it) => it.boq_item_index === idx);
                    return (
                      <TableRow key={`bill-${idx}`}>
                        <TableCell className="font-mono text-[11.5px]">{b.sr_no || idx + 1}</TableCell>
                        <TableCell className="text-[12px]"><div className="max-w-md">{b.description}</div>{b.section && <div className="text-[10px] uppercase text-slate-400">{b.section}</div>}</TableCell>
                        <TableCell className="uppercase text-[11px]">{b.unit}</TableCell>
                        <TableCell className="text-right font-mono-num text-[11.5px]">{b.contract_qty}</TableCell>
                        <TableCell className="text-right font-mono-num text-[11.5px]">{fmtMoney(b.rate)}</TableCell>
                        <TableCell><Input type="number" value={line.supply_this_qty || 0} onChange={(e) => { const v = Number(e.target.value) || 0; if (localIdx >= 0) patchLine(localIdx, "supply_this_qty", v); else setForm((f) => ({ ...f, items: [...f.items, { ...line, supply_this_qty: v }] })); }} className="h-7 text-right font-mono-num w-24" data-testid={`ra-supply-${idx}`} /></TableCell>
                        <TableCell><Input type="number" value={line.install_this_qty || 0} onChange={(e) => { const v = Number(e.target.value) || 0; if (localIdx >= 0) patchLine(localIdx, "install_this_qty", v); else setForm((f) => ({ ...f, items: [...f.items, { ...line, install_this_qty: v }] })); }} className="h-7 text-right font-mono-num w-24" /></TableCell>
                        <TableCell><Input type="number" value={line.tnc_this_qty || 0} onChange={(e) => { const v = Number(e.target.value) || 0; if (localIdx >= 0) patchLine(localIdx, "tnc_this_qty", v); else setForm((f) => ({ ...f, items: [...f.items, { ...line, tnc_this_qty: v }] })); }} className="h-7 text-right font-mono-num w-24" /></TableCell>
                        <TableCell className="text-right font-mono-num font-semibold text-[11.5px]">{fmtMoney(raLine?.total_this_bill_amount || 0)}</TableCell>
                        <TableCell className="text-center">{raLine?.qty_exceeds_boq ? <Warning size={14} className="inline text-red-600" title="Cumulative qty exceeds BOQ" /> : <CheckCircle size={12} className="inline text-emerald-500" />}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Recoveries (Deductions)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="CAR Policy"><Input type="number" value={form.car_policy_amount} onChange={(e) => setForm({ ...form, car_policy_amount: Number(e.target.value) || 0 })} className="font-mono-num text-right" /></Field>
                <Field label="Hold Quality"><Input type="number" value={form.hold_quality} onChange={(e) => setForm({ ...form, hold_quality: Number(e.target.value) || 0 })} className="font-mono-num text-right" /></Field>
                <Field label="Hold Other"><Input type="number" value={form.hold_other} onChange={(e) => setForm({ ...form, hold_other: Number(e.target.value) || 0 })} className="font-mono-num text-right" /></Field>
                <Field label="Hold Other Release"><Input type="number" value={form.hold_other_release} onChange={(e) => setForm({ ...form, hold_other_release: Number(e.target.value) || 0 })} className="font-mono-num text-right" /></Field>
                <Field label="Less Secured Advance"><Input type="number" value={form.less_secured_advance} onChange={(e) => setForm({ ...form, less_secured_advance: Number(e.target.value) || 0 })} className="font-mono-num text-right" /></Field>
                <Field label="Less Advance"><Input type="number" value={form.less_advance} onChange={(e) => setForm({ ...form, less_advance: Number(e.target.value) || 0 })} className="font-mono-num text-right" /></Field>
                <Field label="Already Paid"><Input type="number" value={form.already_paid} onChange={(e) => setForm({ ...form, already_paid: Number(e.target.value) || 0 })} className="font-mono-num text-right" data-testid="ra-already-paid" /></Field>
              </div>
              <div className="grid grid-cols-3 gap-3 pt-2">
                <Field label="Prepared By"><Input value={form.prepared_by} onChange={(e) => setForm({ ...form, prepared_by: e.target.value })} data-testid="ra-prep-by" /></Field>
                <Field label="Verified By"><Input value={form.verified_by} onChange={(e) => setForm({ ...form, verified_by: e.target.value })} /></Field>
                <Field label="Approved By"><Input value={form.approved_by} onChange={(e) => setForm({ ...form, approved_by: e.target.value })} /></Field>
              </div>
              <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
          </div>

          {/* RIGHT — live preview */}
          <div className="space-y-3 sticky top-0 self-start">
            <div className="border border-slate-200 rounded-sm bg-[#0F172A] text-white p-4 space-y-2">
              <div className="text-[10.5px] uppercase tracking-widest opacity-60">Live totals</div>
              <div className="flex justify-between text-[13px]"><span>Gross this bill</span><span className="font-mono-num font-bold">{fmtMoney(preview?.gross_value_this_bill || 0)}</span></div>
              {(preview?.is_interstate) ? (
                <div className="flex justify-between text-[12px] opacity-90"><span>IGST @ 18%</span><span className="font-mono-num">{fmtMoney(preview?.igst_amount || 0)}</span></div>
              ) : (
                <>
                  <div className="flex justify-between text-[12px] opacity-90"><span>CGST @ 9%</span><span className="font-mono-num">{fmtMoney(preview?.cgst_amount || 0)}</span></div>
                  <div className="flex justify-between text-[12px] opacity-90"><span>SGST @ 9%</span><span className="font-mono-num">{fmtMoney(preview?.sgst_amount || 0)}</span></div>
                </>
              )}
              <div className="flex justify-between text-[12.5px] pt-2 border-t border-white/10"><span>Value of payments (A)</span><span className="font-mono-num font-semibold">{fmtMoney(preview?.total_value_of_payments || 0)}</span></div>
              <div className="flex justify-between text-[12px] opacity-90 pt-2"><span>TDS ({preview?.tds_pct}%)</span><span className="font-mono-num">−{fmtMoney(preview?.tds_amount || 0)}</span></div>
              <div className="flex justify-between text-[12px] opacity-90"><span>Retention ({preview?.retention_pct}%)</span><span className="font-mono-num">{preview?.retention_amount < 0 ? `+${fmtMoney(-preview?.retention_amount)}` : `−${fmtMoney(preview?.retention_amount || 0)}`}</span></div>
              <div className="flex justify-between text-[12.5px] pt-2 border-t border-white/10"><span>Total recoveries (B)</span><span className="font-mono-num">−{fmtMoney(preview?.total_recoveries || 0)}</span></div>
              <div className="flex justify-between text-[13px] pt-2 border-t border-white/10"><span>A − B</span><span className="font-mono-num font-semibold">{fmtMoney(preview?.amount_after_recoveries || 0)}</span></div>
              <div className="flex justify-between text-[12px] opacity-90"><span>Already paid</span><span className="font-mono-num">−{fmtMoney(preview?.already_paid || 0)}</span></div>
              <div className="flex justify-between text-[14px] pt-2 mt-1 border-t border-white/20 bg-[#EA580C] -mx-4 -mb-4 px-4 py-3 rounded-b-sm">
                <span className="uppercase font-bold tracking-widest text-[11px]">Amount to Process</span>
                <span className="font-mono-num font-bold">{fmtMoney(preview?.amount_to_be_processed || 0)}</span>
              </div>
            </div>

            <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-2">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 flex items-center gap-2"><Sparkle size={11} className="text-[#EA580C]" /> ITC ledger — per payment terms</div>
              <div className="flex justify-between text-[12px]"><span>Supply</span><span className="font-mono-num">{fmtMoney(preview?.itc_supply_tax || 0)}</span></div>
              <div className="flex justify-between text-[12px]"><span>Installation</span><span className="font-mono-num">{fmtMoney(preview?.itc_install_tax || 0)}</span></div>
              <div className="flex justify-between text-[12px]"><span>Testing / Comm. / Handover</span><span className="font-mono-num">{fmtMoney(preview?.itc_tnc_tax || 0)}</span></div>
              <div className="flex justify-between text-[13px] pt-2 border-t border-slate-200 font-semibold"><span>Total ITC claimable</span><span className="font-mono-num text-emerald-700">{fmtMoney(preview?.itc_total_tax || 0)}</span></div>
              <div className="text-[10.5px] text-slate-400">Taxable base: {fmtMoney(preview?.itc_total_taxable || 0)}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-4 mt-4 border-t border-slate-200 sticky bottom-0 bg-white z-10">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="ra-save"><FloppyDisk size={13} className="mr-1" /> {saveButtonLabel(busy, !!raId)}</Button>
          {raId && (
            <>
              <Button
                variant="outline"
                className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                data-testid="ra-delete"
                title={(existingRA?.status === "submitted" || existingRA?.status === "paid") ? "Submitted / paid bills cannot be deleted — Cancel instead" : "Delete this RA bill"}
                onClick={async () => {
                  const canDelete = existingRA?.status !== "submitted" && existingRA?.status !== "paid";
                  if (canDelete) {
                    if (!window.confirm(`Delete RA bill ${existingRA?.ra_bill_no}? This cannot be undone. Linked measurement sheets will be unlinked.`)) return;
                    try { await api.delete(`/ra-bills/${raId}`); toast.success(`Deleted ${existingRA?.ra_bill_no}`); onSaved?.(); }
                    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
                  } else {
                    if (!window.confirm(`RA bill ${existingRA?.ra_bill_no} is ${existingRA?.status} — it cannot be deleted. Cancel it instead? (Keeps audit history but excludes from cumulative billing.)`)) return;
                    try { await api.post(`/ra-bills/${raId}/status`, { status: "cancelled" }); toast.success(`Cancelled ${existingRA?.ra_bill_no}`); onSaved?.(); }
                    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
                  }
                }}
              >
                <Trash size={13} className="mr-1" /> {(existingRA?.status === "submitted" || existingRA?.status === "paid") ? "Cancel" : "Delete"}
              </Button>
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" onClick={() => triggerFileDownload(`/ra-bills/${raId}/pdf`, `${existingRA?.ra_bill_no || "RA"}.pdf`)} className="rounded-sm"><FilePdf size={13} className="mr-1" /> PDF</Button>
                <Button variant="outline" onClick={() => triggerFileDownload(`/ra-bills/${raId}/excel`, `${existingRA?.ra_bill_no || "RA"}.xlsx`)} className="rounded-sm"><FileXls size={13} className="mr-1" /> Excel</Button>
                <Button className="rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700" onClick={() => changeStatus("verified")} data-testid="ra-verify">Verify</Button>
                <Button className="rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700" onClick={() => changeStatus("approved")} data-testid="ra-approve">Approve</Button>
                <Button className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => changeStatus("submitted")} data-testid="ra-submit">Submit to Client</Button>
                <Button className="rounded-sm bg-[#0F172A] hover:bg-black text-white" onClick={() => changeStatus("paid")} data-testid="ra-mark-paid">Mark Paid</Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
